import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { addHreflangLinks, getCanonicalUrl } from '@/lib/urlHelpers';
import { ArrowLeft, MapPin, Clock, Euro, ExternalLink, Play, Heart, Calendar, Info, AlertCircle, RefreshCw, Phone, Star, Award, Lightbulb, Building2, X } from 'lucide-react';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { PlaceDetailCard, RatingDisplay, OfficialWebsiteButton, AddressBadge } from '@/components/PlaceDetailCard';
import { PlaceAudioSection } from '@/components/PlaceAudioSection';
import { OptimizedImage } from '@/components/OptimizedImage';
import PodcastGenerator from '@/components/PodcastGenerator';
import { NearbyPlaces } from '@/components/NearbyPlaces';
import { PodcastMenuButton } from '@/components/PodcastMenuButton';
import VisitTipsSection from "@/components/VisitTipsSection";
import { useNavigationState } from '@/hooks/useNavigationState';
import { PodcastStorySection } from '@/components/PodcastStorySection';
import PlaceDetailSkeleton from '@/components/PlaceDetailSkeleton';
interface PlaceData {
  Name: string;
  City: string;
  Country: string;
  Continent: string;
  Description_FR: string;
  Description_EN: string;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Image: string | null;
  Latitude: number;
  Longitude: number;
  "Heure d'ouverture"?: string | null;
  "Prix du ticket"?: string | null;
  "Site officiel"?: string | null;
  "Informations clés"?: string | null;
  Type_lieu?: string | null;
  // New fields from scraping - all optional since they may not exist on all records
  Heures?: string | null;
  Prix?: string | null;
  Prix_en?: string | null;
  Site_officiel?: string | null;
  Conseils_visite?: string | null;
  Conseils_visite_en?: string | null;
  Type_Google?: string | null;
  Note_Google?: number | null;
  Adresse?: string | null;
  Téléphone?: string | number | null;
  Informations_clés?: string | null;
  État_ouverture?: string | null;
  // Podcast script fields for SEO enrichment
  Podcast_Script_fr?: string | null;
  Podcast_Script_en?: string | null;
  // Include other database fields that might exist
  AudioURL?: string | null;
  ImageURL?: string | null;
  Erreur?: string | null;
  Feedback?: string | null;
  [key: string]: any; // Allow for other fields from the database
}
const PlaceDetails = () => {
  const {
    placeName
  } = useParams<{
    placeName: string;
  }>();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    language,
    t,
    translateContinent
  } = useTranslation();
  const {
    toggleFavorite,
    isFavorite
  } = useFavorites();
  const {
    isAuthenticated
  } = useAuth();
  const [place, setPlace] = useState<PlaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [showPodcastGenerator, setShowPodcastGenerator] = useState(false);
  const [searchTextForPodcast, setSearchTextForPodcast] = useState('');
  const [preselectedLanguage, setPreselectedLanguage] = useState<'fr' | 'en'>('fr');
  const [showImageModal, setShowImageModal] = useState(false);
  const { navigateBack } = useNavigationState();
  useEffect(() => {
    if (!placeName) return;
    const loadPlaceData = async () => {
      try {
        const decodedPlaceName = decodeURIComponent(placeName);
        const {
          data,
          error
        } = await supabase.from('podcasts').select('*').eq('Name', decodedPlaceName).maybeSingle();
        if (error) throw error;
        if (!data) {
          toast({
            title: t('placeNotFound'),
            description: t('placeNotFoundDesc'),
            variant: "destructive"
          });
          navigate('/');
          return;
        }
        setPlace(data);
      } catch (error) {
        console.error('Error loading place:', error);
        toast({
          title: t('errorTitle'),
          description: t('loadingPlaceError'),
          variant: "destructive"
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadPlaceData();
  }, [placeName, navigate, toast]);

  // SEO optimization - Update title and meta description when place data loads
  useEffect(() => {
    if (place) {
      // Optimize page title with location and keywords (50-60 chars)
      const titleText = language === 'FR' 
        ? `${place.Name}, ${place.City}: Histoire en Podcast - DiskOver`
        : `${place.Name}, ${place.City}: History and Audio Podcast - DiskOver`;
      document.title = titleText.length > 60 
        ? titleText.substring(0, 57) + '...' 
        : titleText;
      
      // Get place description based on language
      const placeDescription = language === 'FR' 
        ? (place.Description_FR || place.Conseils_visite || place.Informations_clés)
        : (place.Description_EN || place.Description_FR || place.Conseils_visite || place.Informations_clés);
      
      // Create optimized meta description (150-160 chars) with place name and CTA
      let metaDescriptionContent;
      
      if (placeDescription && placeDescription.length > 40) {
        // Use place description with CTA
        const descriptionPrefix = placeDescription.substring(0, 110);
        metaDescriptionContent = language === 'FR'
          ? `${descriptionPrefix}... Podcast 3 min, infos pratiques et conseils de visite.`
          : `${descriptionPrefix}... 3-min podcast, practical info and visit tips.`;
      } else {
        // Fallback with specific structure
        metaDescriptionContent = language === 'FR'
          ? `Découvrez l'histoire de ${place.Name} à travers un podcast immersif de 3 minutes. Infos pratiques, activités et conseils pour préparer votre visite.`
          : `Discover the history of ${place.Name} through a 3-minute immersive podcast. Practical info, activities and visit tips to prepare your discovery.`;
      }
      
      // Ensure description is within 150-160 chars
      if (metaDescriptionContent.length > 160) {
        metaDescriptionContent = metaDescriptionContent.substring(0, 157) + '...';
      }
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', metaDescriptionContent);
      
      // Update canonical link to point to this page's own URL
      const placePath = `/place/${encodeURIComponent(place.Name)}`;
      const canonicalUrl = getCanonicalUrl(placePath);
      
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonicalUrl);
      
      // Add hreflang links for multilingual support
      addHreflangLinks(placePath);
      
      // Update Open Graph metadata with place-specific content
      const ogTitle = language === 'FR'
        ? `${place.Name}: Découvrez son Histoire en Podcast`
        : `${place.Name}: Discover its History in Podcast`;
      
      const ogDescription = language === 'FR'
        ? `Explorez ${place.Name} avec un podcast culturel immersif. Infos pratiques et conseils de visite.`
        : `Explore ${place.Name} with an immersive cultural podcast. Practical info and visit tips.`;
      
      updateOpenGraphMeta('og:title', ogTitle);
      updateOpenGraphMeta('og:description', ogDescription);
      updateOpenGraphMeta('og:url', canonicalUrl);
      updateOpenGraphMeta('og:type', 'article');
      
      // Use real place image, not logo
      if (place.Image) {
        updateOpenGraphMeta('og:image', place.Image);
        updateOpenGraphMeta('og:image:alt', `${place.Name} - ${place.City}, ${place.Country}`);
      } else {
        updateOpenGraphMeta('og:image', 'https://diskover.ovh/og-image.jpg');
        updateOpenGraphMeta('og:image:alt', 'DiskOver - Cultural Podcasts');
      }
      
      // Update Twitter metadata
      updateOpenGraphMeta('twitter:card', 'summary_large_image');
      updateOpenGraphMeta('twitter:title', ogTitle);
      updateOpenGraphMeta('twitter:description', ogDescription);
      
      if (place.Image) {
        updateOpenGraphMeta('twitter:image', place.Image);
        updateOpenGraphMeta('twitter:image:alt', `${place.Name} - ${place.City}, ${place.Country}`);
      } else {
        updateOpenGraphMeta('twitter:image', 'https://diskover.ovh/og-image.jpg');
        updateOpenGraphMeta('twitter:image:alt', 'DiskOver - Cultural Podcasts');
      }
      
      // Add structured data (JSON-LD) for the place
      addStructuredData(place, canonicalUrl);
      
      // Add data-snap-ready attribute for react-snap
      document.body.setAttribute('data-snap-ready', 'true');
    }
  }, [place, language]);

  // Helper function to update Open Graph and Twitter meta tags
  const updateOpenGraphMeta = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) || 
               document.querySelector(`meta[name="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      if (property.startsWith('twitter:')) {
        meta.setAttribute('name', property);
      } else {
        meta.setAttribute('property', property);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Helper function to add structured data (JSON-LD) for SEO
  const addStructuredData = (place: PlaceData, canonicalUrl: string) => {
    // Remove existing structured data for places
    const existingScript = document.querySelector('script[data-place-structured-data]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": place.Name,
      "description": place.Description_FR || place.Description_EN,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": place.City,
        "addressCountry": place.Country,
        "streetAddress": place.Adresse
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": place.Latitude,
        "longitude": place.Longitude
      },
      "url": canonicalUrl,
      "image": place.Image || 'https://diskover.ovh/logo.png',
      "telephone": place.Téléphone ? String(place.Téléphone) : undefined,
      "openingHours": place.Heures || place["Heure d'ouverture"],
      "priceRange": place.Prix || place["Prix du ticket"],
      "additionalType": place.Type_Google || place.Type_lieu,
      "sameAs": place.Site_officiel || place["Site officiel"]
    };

    // Remove undefined values
    Object.keys(structuredData).forEach(key => {
      if (structuredData[key as keyof typeof structuredData] === undefined) {
        delete structuredData[key as keyof typeof structuredData];
      }
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-place-structured-data', 'true');
    script.textContent = JSON.stringify(structuredData, null, 2);
    document.head.appendChild(script);
  };
  const handleScrapePlaceInfo = async () => {
    if (!place?.["Site officiel"]) {
      toast({
        title: t('missingOfficialSite'),
        description: t('missingOfficialSiteDesc'),
        variant: "destructive"
      });
      return;
    }
    setScrapingLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('scrape-place-info', {
        body: {
          placeName: place.Name,
          officialWebsite: place["Site officiel"]
        }
      });
      if (error) throw error;

      // Refresh place data
      const {
        data: updatedData,
        error: fetchError
      } = await supabase.from('podcasts').select('*').eq('Name', place.Name).single();
      if (fetchError) throw fetchError;
      setPlace(updatedData);
      toast({
        title: t('infoUpdated'),
        description: t('infoUpdatedDesc')
      });
    } catch (error) {
      console.error('Error scraping place info:', error);
      toast({
        title: t('errorTitle'),
        description: t('scrapingError'),
        variant: "destructive"
      });
    } finally {
      setScrapingLoading(false);
    }
  };

  // Handle opening podcast generator
  const handleOpenPodcastGenerator = (locationName: string, language: 'fr' | 'en') => {
    setSearchTextForPodcast(locationName);
    setPreselectedLanguage(language);
    setShowPodcastGenerator(true);
  };

  // Handle podcast generation success
  const handlePodcastGenerated = async () => {
    setShowPodcastGenerator(false);
    setSearchTextForPodcast('');
    setPreselectedLanguage('fr');
    
    // Refresh place data to get the new podcast
    if (place) {
      try {
        const { data: updatedData, error } = await supabase
          .from('podcasts')
          .select('*')
          .eq('Name', place.Name)
          .single();
        
        if (error) throw error;
        setPlace(updatedData);
        
        toast({
          title: t('podcastCreated'),
          description: t('podcastCreatedDesc'),
        });
      } catch (error) {
        console.error('Error refreshing place data:', error);
      }
    }
  };
  if (loading) {
    return <PlaceDetailSkeleton onBack={() => navigateBack()} />;
  }
  if (!place) return null;

  // Combine old and new field names for compatibility with language support
  const openingHours = place.Heures || place["Heure d'ouverture"];
  const pricing = language === 'EN' && place.Prix_en 
    ? place.Prix_en 
    : (place.Prix || place["Prix du ticket"]);
  const officialSite = place.Site_officiel || place["Site officiel"];
  const keyInfo = place.Informations_clés || place["Informations clés"];
  const visitTips = language === 'EN' && place.Conseils_visite_en 
    ? place.Conseils_visite_en 
    : place.Conseils_visite;
  const placeType = place.Type_Google || place.Type_lieu;
  const phoneNumber = place.Téléphone;
  const address = place.Adresse;
  const rating = place.Note_Google;
  const openingStatus = place.État_ouverture;
  const hasDetailedInfo = openingHours || pricing || keyInfo || visitTips || officialSite;
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-muted/20 overflow-x-hidden">
      {/* Hero Header */}
      <div className="relative w-full">
        {/* Hero Image */}
        <div className="h-56 sm:h-64 md:h-72 w-full overflow-hidden relative cursor-pointer" onClick={() => place.Image && setShowImageModal(true)}>
          {place.Image ? <OptimizedImage src={place.Image} alt={`${place.Name}, ${place.City}`} className="w-full h-full hover:scale-105 transition-transform duration-300" imgClassName="w-full h-full object-cover object-center" width={1200} height={288} loading="eager" /> : <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
              <MapPin className="w-16 h-16 sm:w-20 sm:h-20 text-white/80" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          {place.Image && (
            <div className="absolute top-4 right-4 bg-black/20 backdrop-blur-sm rounded-full p-2 opacity-0 hover:opacity-100 transition-opacity duration-200">
              <ExternalLink className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        {/* Floating Navigation */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-40">
          <Button
            variant="glass"
            size="icon"
            onClick={() => navigateBack()}
            aria-label={language === 'FR' ? 'Retour' : 'Back'}
            className="w-10 h-10 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              size="icon" 
              variant="glass" 
              className={`w-10 h-10 rounded-full transition-all duration-200 ${
                isFavorite(place.Name) 
                  ? 'bg-red-500/20 text-red-600 border-red-500/40 hover:bg-red-500/25' 
                  : 'hover:bg-foreground/20'
              }`} 
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/auth');
                  return;
                }
                toggleFavorite(place.Name);
              }}
              aria-label={isFavorite(place.Name) ? `Retirer ${place.Name} des favoris` : `Ajouter ${place.Name} aux favoris`}
            >
              <Heart className={`w-4 h-4 ${isFavorite(place.Name) ? 'fill-current' : ''}`} />
            </Button>
            <PodcastMenuButton 
              podcastName={place.Name}
              className="bg-foreground/10 text-foreground border border-border/40 backdrop-blur-md hover:bg-foreground/15 w-10 h-10 rounded-full"
            />
          </div>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white w-full">
          <div className="w-full">
            <div className="mb-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 drop-shadow-lg leading-tight">
                {place.Name}
              </h1>
              <div className="flex flex-col gap-1 text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{place.City}, {place.Country}</span>
                </div>
                {rating && <div className="flex items-center"><RatingDisplay rating={rating} /></div>}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1 sm:gap-1.5">
              {placeType && !placeType.startsWith('Favorites, 0ah') && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Badge 
                      variant="secondary" 
                      className="bg-white/20 text-white border-white/30 text-xs px-2 py-0.5 cursor-pointer hover:bg-white/30 transition-all duration-200 hover:scale-105 active:scale-95"
                      title="Cliquez pour voir le type complet"
                    >
                      <Building2 className="w-2.5 h-2.5 mr-1" />
                      <span className="truncate max-w-24">{placeType}</span>
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="z-50 max-w-sm p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl rounded-lg backdrop-blur-sm" 
                    side="top"
                    sideOffset={8}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold text-foreground">Type de lieu</p>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed break-words">
                        {placeType}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 text-xs px-2 py-0.5">
                {translateContinent(place.Continent)}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full px-3 py-4 sm:px-4 sm:py-6 -mt-6 sm:-mt-8 relative z-10">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 w-full max-w-none">
          {/* Left Column - Audio & Quick Info */}
          <div className="lg:col-span-1 space-y-4">
            {/* Audio Player Section */}
            <PlaceAudioSection place={place} language={language} onOpenPodcastGenerator={handleOpenPodcastGenerator} />

            {/* Quick Actions */}
            <Card className="overflow-hidden">
              <div className="p-6">
                <h2 className="font-semibold text-base mb-6">{t('quickActions')}</h2>
                
                <div className="flex justify-around items-center">
                  {officialSite && <div className="flex flex-col items-center group cursor-pointer" onClick={() => window.open(officialSite, '_blank', 'noopener,noreferrer')}>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-150 group-hover:scale-95 group-active:scale-90 shadow-sm group-hover:shadow-inner bg-blue-100 group-hover:bg-blue-200 dark:bg-blue-950/30 dark:group-hover:bg-blue-900/50">
                        <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{t('siteLabel')}</span>
                    </div>}

                  {address && <div className="flex flex-col items-center group cursor-pointer" onClick={() => {
                  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
                  window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                }}>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-150 group-hover:scale-95 group-active:scale-90 shadow-sm group-hover:shadow-inner bg-green-100 group-hover:bg-green-200 dark:bg-green-950/30 dark:group-hover:bg-green-900/50">
                        <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{t('addressLabel')}</span>
                    </div>}

                  {phoneNumber && <div className="flex flex-col items-center group cursor-pointer" onClick={() => window.open(`tel:${phoneNumber}`, '_self')}>
                      <div className="w-12 h-12 flex items-center justify-center mb-2 transition-all duration-150 group-hover:scale-95 group-active:scale-90 shadow-sm group-hover:shadow-inner bg-purple-100 group-hover:bg-purple-200 dark:bg-purple-950/30 dark:group-hover:bg-purple-900/50 rounded-full">
                        <Phone className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{t('phoneLabel')}</span>
                    </div>}
                </div>
              </div>
            </Card>

            {/* À proximité - Desktop only */}
            <NearbyPlaces 
              currentPlace={{
                Name: place.Name,
                Latitude: place.Latitude,
                Longitude: place.Longitude
              }}
              className="hidden lg:block"
            />
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Description */}
            <PlaceDetailCard title={t('aboutTitle')} icon={<Info className="w-5 h-5" />}>
              <p className="text-muted-foreground leading-relaxed text-sm">
                {language === 'FR' ? place.Description_FR : place.Description_EN || place.Description_FR}
              </p>
            </PlaceDetailCard>

            {/* Practical Information */}
            {hasDetailedInfo && <PlaceDetailCard title={t('practicalInfoTitle')} icon={<Clock className="w-5 h-5" />} className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
                <div className="grid gap-4">
                  {openingHours && <div className="space-y-1">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <Clock className="w-3 h-3 text-primary" />
                        {t('scheduleLabel')}
                      </div>
                      <p className="text-xs text-muted-foreground pl-5">{openingHours}</p>
                    </div>}

                  {pricing && <div className="space-y-1">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <Euro className="w-3 h-3 text-primary" />
                        {t('pricingLabel')}
                      </div>
                      <p className="text-xs text-muted-foreground pl-5">{pricing}</p>
                    </div>}
                </div>
              </PlaceDetailCard>}


            {/* Visit Tips */}
            {visitTips && <PlaceDetailCard title={t('visitTipsTitle')} icon={<Lightbulb className="w-5 h-5" />} className="bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
                <div className="text-muted-foreground whitespace-pre-line text-sm">
                  {visitTips}
                </div>
              </PlaceDetailCard>}

            {/* Visit Tips Section */}
            <VisitTipsSection placeName={place.Name} />

            {/* No detailed info fallback */}
            {!hasDetailedInfo && <Card className="p-6 text-center bg-gradient-to-br from-muted/20 to-muted/10 border-dashed">
                <AlertCircle className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                <p className="font-semibold mb-2 text-sm">{t('infoCollectionTitle')}</p>
                <p className="text-muted-foreground mb-3 text-xs">
                  {t('infoCollectionDesc')}
                </p>
                {place["Site officiel"] || place.Site_officiel ? <Button variant="outline" size="sm" onClick={handleScrapePlaceInfo} disabled={scrapingLoading}>
                    {scrapingLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    <span className="text-xs">{t('refreshInfoButton')}</span>
                  </Button> : <p className="text-xs text-muted-foreground">
                    {t('noOfficialSite')}
                  </p>}
              </Card>}
          </div>
        </div>

        {/* À proximité - Mobile only (en bas) */}
        <div className="mt-6 lg:hidden">
          <NearbyPlaces 
            currentPlace={{
              Name: place.Name,
              Latitude: place.Latitude,
              Longitude: place.Longitude
            }}
          />
        </div>

        {/* Podcast Story Section - SEO enrichment */}
        <div className="mt-6">
          <PodcastStorySection 
            placeName={place.Name}
            script={language === 'FR' ? (place.Podcast_Script_fr || '') : (place.Podcast_Script_en || place.Podcast_Script_fr || '')}
            language={language}
          />
        </div>
      </main>

      {/* Image Modal */}
      {place.Image && (
        <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 border-0 bg-transparent">
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="relative max-w-full max-h-[80vh]">
                <OptimizedImage
                  src={place.Image}
                  alt={`${place.Name}, ${place.City}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  width={1200}
                  height={800}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowImageModal(false)}
                  className="absolute top-4 right-4 bg-black/20 hover:bg-black/40 text-white border-0 backdrop-blur-sm rounded-full w-8 h-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Podcast Generator Modal */}
      <PodcastGenerator
        isOpen={showPodcastGenerator}
        onClose={() => {
          setShowPodcastGenerator(false);
          setSearchTextForPodcast('');
          setPreselectedLanguage('fr');
        }}
        onPodcastGenerated={handlePodcastGenerated}
        initialSearchText={searchTextForPodcast}
        preselectedLanguage={preselectedLanguage}
      />
    </div>
  );
};
export default PlaceDetails;